import { EngineIcon } from '@phosphor-icons/react';

import type { MapCluster } from '@/lib/maps/clustering';
import { MARKER_PIN_SIZE_PX } from '@/lib/maps/markers';

import styles from './cluster-markers.module.scss';
import { FieldAreaSymbol } from './legend-symbols';
import { MapOverlay } from './map-overlay';

export type ClusterMarkersProps = {
  /** Which population these clusters count. Drives the head symbol and the accessible name only. */
  kind: 'device' | 'field';
  clusters: readonly MapCluster[];
  /** The whole cluster, so the caller needs no index to answer the click (113586 §11e, §14d). */
  onSelect: (cluster: MapCluster) => void;
};

/**
 * The count markers that stand in for collapsed groups (Figma `823:702498`, `864:556413`).
 *
 * **One component, two populations, never mixed.** 113586 §14e forbids a Field Area being counted in
 * a Device cluster or the reverse, and that holds because each list comes from its own index — this
 * layer only draws what it is handed and counts nothing itself. `kind` picks the head: the Device pin
 * (Figma `823:702487`) or the Field Area legend swatch at marker scale (§14b — Figma draws no field
 * cluster, see the plan's Design spec), with the badge in the same position either way.
 *
 * The click never reaches the map. This is a real `<button>` inside `<MapOverlay>`'s portal, so it
 * swallows its own click before `<MapCanvas onMapClick>` fires — which is how "clicking a cluster
 * opens no details panel" (§11e), "focuses nothing" (§14d) and "keeps the Field focused and its panel
 * open" (§12b) are all delivered by the caller dispatching **nothing at all**.
 *
 * It lives in `components/maps` rather than in a feature because it draws BOTH populations: in
 * `features/devices` it would have to reach into `features/fields` for the Field Area symbol, and in
 * `features/fields` the reverse. `<FieldAreaSymbol>` is already here for exactly this second consumer.
 */
export function ClusterMarkers({ kind, clusters, onSelect }: ClusterMarkersProps) {
  return (
    <>
      {clusters.map((cluster) => (
        <MapOverlay key={cluster.id} point={cluster.center}>
          <button
            type="button"
            className={kind === 'device' ? `${styles.cluster} ${styles.round}` : styles.cluster}
            aria-label={`${cluster.count} ${kind === 'field' ? 'field areas' : 'devices'}`}
            onClick={() => onSelect(cluster)}
          >
            {kind === 'field' ? (
              <FieldAreaSymbol size={MARKER_PIN_SIZE_PX} />
            ) : (
              <span className={styles.pin} aria-hidden>
                <EngineIcon size={20} />
              </span>
            )}
            <span className={styles.count}>{cluster.count}</span>
          </button>
        </MapOverlay>
      ))}
    </>
  );
}
